import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "@/lib/checkout";
import { LogoMark } from "@/components/Logo";

const prettyTime = (t) => {
  const [h, m] = t.split(":").map(Number);
  const am = h < 12;
  return `${(h + 11) % 12 + 1}:${String(m).padStart(2, "0")} ${am ? "AM" : "PM"}`;
};

export default function SchedulePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const intakeId = params.get("intake") || "";
  const sessionId = params.get("session") || "";
  const type = params.get("type") || "full";

  const dates = useMemo(() => {
    const out = [];
    const d = new Date();
    while (out.length < 10) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) out.push(new Date(d));
    }
    return out;
  }, []);

  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const [date, setDate] = useState(fmt(dates[0]));
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let active = true;
    setSlots(null);
    setTime("");
    axios
      .get(`${API_BASE}/slots/${date}`)
      .then(({ data }) => {
        if (active) setSlots(data.slots);
      })
      .catch(() => {
        if (active) {
          setSlots([]);
          toast.error("Could not load available times. Please try again.");
        }
      });
    return () => {
      active = false;
    };
  }, [date]);

  const confirm = async () => {
    if (!time || confirming) return;
    setConfirming(true);
    try {
      await axios.post(`${API_BASE}/booking`, {
        intake_id: intakeId,
        session_id: sessionId,
        date,
        time,
      });
      navigate(`/thank-you?type=${type}&date=${encodeURIComponent(date)}&time=${encodeURIComponent(time)}`);
    } catch (err) {
      setConfirming(false);
      toast.error(err?.response?.data?.detail || "Booking failed. Please pick another time.");
      axios
        .get(`${API_BASE}/slots/${date}`)
        .then(({ data }) => setSlots(data.slots))
        .catch(() => {});
    }
  };

  return (
    <div className="min-h-screen bg-obsidian px-5 py-16 sm:py-20 grain">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="schedule-back-link"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> All In Hand Marketing
        </Link>

        <div className="mt-10 rounded-3xl border border-gold/20 bg-obsidian-800/60 p-7 sm:p-12">
          <LogoMark className="w-12 h-12" />
          <h1 className="font-display text-2xl sm:text-4xl font-bold text-white mt-6" data-testid="schedule-heading">
            {type === "consultation" ? "Schedule Your Consultation" : "Schedule Your Strategy Session"}
          </h1>
          <p className="text-slate-400 mt-4 text-sm sm:text-base">
            Pick a day and a time that works for you. Sessions run Monday through Friday.
          </p>

          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500 mt-10 mb-4">
            Choose a day
          </p>
          <div className="flex gap-2.5 overflow-x-auto pb-3 -mx-1 px-1">
            {dates.map((d, i) => {
              const val = fmt(d);
              const active = val === date;
              return (
                <button
                  key={val}
                  data-testid={`schedule-date-${i}`}
                  onClick={() => setDate(val)}
                  className={`shrink-0 rounded-xl border px-4 py-3 text-center transition-all duration-300 ${
                    active
                      ? "border-gold bg-gold/10 text-gold"
                      : "border-slate-700 text-slate-300 hover:border-gold/50"
                  }`}
                >
                  <span className="block text-[10px] font-mono uppercase tracking-widest opacity-80">
                    {d.toLocaleDateString("en-US", { weekday: "short" })}
                  </span>
                  <span className="block font-display font-bold text-sm mt-1">
                    {d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                </button>
              );
            })}
          </div>

          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500 mt-8 mb-4">
            Choose a time
          </p>
          {slots === null ? (
            <div data-testid="schedule-slots-loading" className="flex items-center gap-3 text-slate-400 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin text-gold" /> Loading available times…
            </div>
          ) : slots.every((s) => !s.available) ? (
            <p data-testid="schedule-slots-full" className="text-slate-400 text-sm py-6">
              No openings left on this day — please choose another day.
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="schedule-slot-grid">
              {slots.map((s) => (
                <button
                  key={s.time}
                  data-testid={`schedule-slot-${s.time}`}
                  disabled={!s.available}
                  onClick={() => setTime(s.time)}
                  className={`rounded-xl border px-3 py-3 text-sm transition-all duration-300 ${
                    time === s.time
                      ? "border-gold bg-gold/10 text-gold font-semibold"
                      : s.available
                      ? "border-slate-700 text-slate-300 hover:border-gold/50"
                      : "border-slate-800 text-slate-700 line-through cursor-not-allowed"
                  }`}
                >
                  {prettyTime(s.time)}
                </button>
              ))}
            </div>
          )}

          <button
            data-testid="schedule-confirm"
            onClick={confirm}
            disabled={!time || confirming}
            className="mt-10 w-full rounded-full bg-gold text-obsidian font-display font-bold py-4 hover:bg-gold-bright transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
            {time ? `Confirm ${new Date(`${date}T${time}`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} at ${prettyTime(time)}` : "Select a time to confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
